<script setup>
/**
 * The host composer used by both the Chat and Q&A panes.
 *
 * A textarea rather than FormInput (which only renders <input>): hosts can
 * write up to 500 characters, and a single thin line is a poor place to do
 * that. It starts at one comfortable row and grows with the draft to three
 * lines, then scrolls internally — the message list above keeps its height
 * priority. Enter sends, Shift+Enter breaks a line.
 */
import { ref, computed, watch, nextTick } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'

const props = defineProps({
	modelValue: {
		type: String,
		default: ''
	},
	maxlength: {
		type: Number,
		required: true
	},
	canPost: {
		type: Boolean,
		default: false
	},
	placeholder: {
		type: String,
		default: ''
	},
	/** Shown in place of the placeholder when posting is not possible. */
	blockedReason: {
		type: String,
		default: ''
	},
	sendError: {
		type: String,
		default: ''
	},
	submitLabel: {
		type: String,
		default: 'Send'
	},
	/** Characters left at which the countdown becomes worth showing. */
	counterAt: {
		type: Number,
		default: 80
	}
})

const emit = defineEmits(['update:modelValue', 'submit'])

const boxRef = ref(null)

// Three lines of leading-5 text plus the vertical padding; past this the
// textarea stops growing and scrolls inside itself.
const MAX_HEIGHT = 76

const resize = () => {
	const el = boxRef.value
	if (!el) return

	// Collapse first so a shortened draft can shrink the box back down;
	// scrollHeight only ever reports growth otherwise.
	el.style.height = 'auto'
	el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`
	el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden'
}

// Covers programmatic changes too (the parent clearing the draft on send),
// not just keystrokes.
watch(() => props.modelValue, () => nextTick(resize))

const remaining = computed(() => props.maxlength - props.modelValue.length)

const onKeydown = (event) => {
	if (event.key !== 'Enter' || event.shiftKey) return
	event.preventDefault()
	emit('submit')
}

const boxClass = computed(() => [
	'w-full bg-surface-3 focus:bg-surface-4 rounded px-3 py-2 text-base md:text-tiny',
	'placeholder:text-tiny text-surface-text-2 disabled:text-surface-7 resize-none leading-5',
	'focus:outline-none focus:ring-0 transition-color',
	props.sendError ? 'ring-1 ring-error-light bg-error-transparent' : ''
])
</script>

<template>
	<form @submit.prevent="emit('submit')">
		<p v-if="sendError" class="text-tiny text-error mb-1.5">{{ sendError }}</p>

		<div class="flex items-end gap-x-2">
			<textarea ref="boxRef" :value="modelValue" rows="1" :class="boxClass"
				:maxlength="maxlength" :disabled="!canPost" enterkeyhint="send" dir="auto"
				:placeholder="canPost ? placeholder : blockedReason"
				:aria-label="placeholder"
				@input="emit('update:modelValue', $event.target.value)"
				@keydown="onKeydown"></textarea>
			<!-- !h-9 splits the difference between FormButton's md (h-8) and
			lg (h-10) so the button matches the textarea's one-row height. -->
			<FormButton type="main" size="md" class="!h-9" html-type="submit"
				:disabled="!canPost || !modelValue.trim()">
				{{ submitLabel }}
			</FormButton>
		</div>

		<p v-if="remaining <= counterAt" class="text-tiny text-surface-7 text-right mt-1 mb-0"
			:class="{ '!text-error': remaining <= 0 }">
			{{ remaining }} characters left
		</p>
	</form>
</template>
